import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, CreditCard, Wallet, ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import {
  getPackages,
  getPaymentConfig,
  createCheckoutSession,
  getCheckoutStatus,
} from "../lib/api";

const FEATURES = {
  brand_film: ["Concept & treatment", "1 shoot day", "Cinema-grade grade", "60s + cutdowns"],
  commercial: ["Full creative direction", "2 shoot days", "Casting & location", "Broadcast delivery"],
  documentary: ["Story development", "Multi-day shoot", "Interviews & vérité", "Long-form edit"],
  deposit: ["Reserve your slot", "Applied to final invoice", "Priority scheduling", "Fully refundable*"],
};

const PollingBanner = ({ state, pkgName }) => {
  if (!state) return null;
  const map = {
    checking: { icon: <Loader2 className="animate-spin" size={18} />, text: "Confirming your payment…", cls: "text-sand-400" },
    paid: { icon: <Check size={18} />, text: `Payment received — thank you! (${pkgName || "Package"})`, cls: "text-sand-700" },
    expired: { icon: <X size={18} />, text: "Payment session expired. Please try again.", cls: "text-sand-400" },
    error: { icon: <X size={18} />, text: "We couldn't confirm the payment. Check your email or contact us.", cls: "text-sand-400" },
  };
  const c = map[state] || map.checking;
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-3 border border-border bg-surface px-6 py-4 ${c.cls}`}
      data-testid="payment-status-banner"
    >
      {c.icon}<span className="font-body">{c.text}</span>
    </motion.div>
  );
};

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [config, setConfig] = useState({ stripe: true, paypal: false, wave: false, wave_link: null });
  const [loadingId, setLoadingId] = useState(null);
  const [payState, setPayState] = useState(null);
  const [paidPkg, setPaidPkg] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    getPackages().then((all) => setPackages(all.filter((p) => !p.id.startsWith("wedding_")))).catch(() => toast.error("Couldn't load packages."));
    getPaymentConfig().then(setConfig).catch(() => {});
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    const MAX = 6;
    if (attempt >= MAX) { setPayState("error"); return; }
    try {
      const res = await getCheckoutStatus(sessionId);
      if (res.payment_status === "paid") {
        setPayState("paid"); setPaidPkg(res.package_name);
        toast.success("Payment successful — thank you!");
        return;
      }
      if (res.status === "expired") { setPayState("expired"); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    }
  }, []);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) {
      setPayState("checking");
      pollStatus(sessionId);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const payWithStripe = async (id) => {
    setLoadingId(id);
    try {
      const { url } = await createCheckoutSession(id);
      if (url) window.location.href = url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  const payWithPayPal = () => {
    if (config.paypal) {
      toast.info("Opening PayPal…");
    } else {
      toast.info("PayPal will be available once account credentials are connected.");
    }
  };

  const payWithWave = () => {
    if (config.wave && config.wave_link) {
      window.open(config.wave_link, "_blank", "noopener");
    } else {
      toast.info("Wave payments will be available once your Wave payment link is connected.");
    }
  };

  const fmt = (a, c) => new Intl.NumberFormat("en-US", { style: "currency", currency: (c || "usd").toUpperCase(), maximumFractionDigits: 0 }).format(a);

  return (
    <div data-testid="pricing-page">
      <section className="mx-auto max-w-7xl px-6 pb-14 pt-40 lg:px-10 lg:pt-48">
        <Reveal>
          <p className="overline text-sand-700">Packages & Payment</p>
          <h1 className="mt-6 font-display text-6xl leading-[0.9] tracking-tight text-sand-100 md:text-8xl">
            Invest in your <span className="italic text-sand-700">story.</span>
          </h1>
          <p className="mt-8 max-w-xl font-body text-lg font-light text-sand-400">
            Transparent packages, secure checkout. Pay by card, PayPal, or Wave — reserve your production today.
          </p>
        </Reveal>

        {payState && (
          <div className="mt-10"><PollingBanner state={payState} pkgName={paidPkg} /></div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-10">
        <div className="grid gap-px border border-border md:grid-cols-2 lg:grid-cols-4">
          {packages.map((p, i) => (
            <motion.div
              key={p.id}
              className="flex flex-col bg-surface p-8 transition-colors duration-500 hover:bg-[#1d1815]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`package-card-${p.id}`}
            >
              <span className="font-display text-2xl italic text-sand-700">0{i + 1}</span>
              <h3 className="mt-4 font-display text-3xl text-sand-100">{p.name}</h3>
              <p className="mt-4 font-display text-5xl text-sand-100">{fmt(p.amount, p.currency)}</p>
              <ul className="mt-8 flex-1 space-y-3">
                {(FEATURES[p.id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-3 font-body text-sm font-light text-sand-400">
                    <Check size={16} className="mt-0.5 shrink-0 text-sand-700" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => payWithStripe(p.id)}
                disabled={loadingId === p.id}
                className="btn-desert mt-8 w-full justify-center"
                data-testid={`pay-stripe-${p.id}`}
              >
                {loadingId === p.id ? <><Loader2 size={16} className="animate-spin" /> Redirecting</> : <><CreditCard size={16} /> Pay with card</>}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-32 lg:px-10">
        <Reveal>
          <div className="border border-border bg-surface p-10 lg:p-14">
            <p className="overline text-sand-700">More ways to pay</p>
            <h2 className="mt-6 font-display text-4xl text-sand-100 md:text-5xl">Choose what suits you</h2>
            <p className="mt-4 max-w-xl font-body font-light text-sand-400">
              Prefer PayPal or Wave? Pick a package above, then complete payment through your preferred provider.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={payWithPayPal} className="btn-desert" data-testid="pay-paypal">
                <Wallet size={16} /> Pay with PayPal {!config.paypal && "(soon)"}
              </button>
              <button onClick={payWithWave} className="btn-desert" data-testid="pay-wave">
                <ArrowUpRight size={16} /> Pay with Wave {!config.wave && "(soon)"}
              </button>
            </div>
            <p className="mt-6 text-xs text-sand-400/70">
              Card payments are processed securely via Stripe. *Deposits are refundable up to 14 days before the shoot.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
